/** Consentimiento de cookies de analítica (Google Analytics 4, Consent Mode v2).
 *
 *  Idea 286 del área 03. Reglas:
 *   - Por defecto TODO está denegado (lo declara el script de `index.html` ANTES de cargar gtag.js).
 *   - Solo «Aceptar» concede `analytics_storage`. Las señales de publicidad (`ad_storage`,
 *     `ad_user_data`, `ad_personalization`) quedan SIEMPRE denegadas: CP no hace publicidad con GA4.
 *   - La elección se guarda en localStorage con fecha y versión del texto del banner. Si la versión
 *     cambia (CONSENT_TEXT_VERSION), se vuelve a preguntar.
 *   - Revocable: «Preferencias de cookies» (pie del sitio) reabre el banner.
 *   - Microsoft Clarity NO está instalado ni se activa aquí.
 *
 *  `index.html` repite la clave y la versión en su script en línea (necesita leer la elección antes
 *  de que cargue nada); `scripts/test-cookie-consent.ts` comprueba que ambos lados coinciden. */

export const CONSENT_KEY = "cp_cookie_consent";
/** Subir este número cuando cambie el texto del banner o lo que se mide: vuelve a preguntar a todos. */
export const CONSENT_TEXT_VERSION = "1";
/** Evento de ventana con el que el enlace «Preferencias de cookies» reabre el banner. */
export const CONSENT_OPEN_EVENT = "cp-cookie-prefs-open";

export type ConsentChoice = "granted" | "denied";
export interface StoredConsent { choice: ConsentChoice; date: string; version: string }

/** Lo mínimo de Storage que se usa (permite probar la lógica sin navegador). */
export interface StorageLike { getItem(k: string): string | null; setItem(k: string, v: string): void; removeItem(k: string): void }

export type GtagFn = (...args: unknown[]) => void;

/** Estado inicial: todo denegado. Es lo que declara `index.html`. */
export const CONSENT_DEFAULT = {
  ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied", analytics_storage: "denied",
} as const;

export function consentSignals(choice: ConsentChoice) {
  return { ...CONSENT_DEFAULT, analytics_storage: choice } as Record<string, string>;
}

/** Elección guardada y vigente, o null si no hay, está dañada o es de otra versión del texto. */
export function readConsent(storage: StorageLike | null): StoredConsent | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<StoredConsent>;
    if (!v || (v.choice !== "granted" && v.choice !== "denied")) return null;
    if (v.version !== CONSENT_TEXT_VERSION || typeof v.date !== "string") return null;
    return { choice: v.choice, date: v.date, version: v.version };
  } catch { return null; }
}

/** ¿Hay que mostrar el banner? Sí cuando no hay una elección vigente. */
export function shouldAsk(storage: StorageLike | null): boolean {
  return readConsent(storage) === null;
}

export function saveConsent(storage: StorageLike | null, choice: ConsentChoice, now: Date = new Date()): StoredConsent {
  const v: StoredConsent = { choice, date: now.toISOString(), version: CONSENT_TEXT_VERSION };
  try { storage?.setItem(CONSENT_KEY, JSON.stringify(v)); } catch { /* sin almacenamiento: se pregunta de nuevo la próxima vez */ }
  return v;
}

/** Revocar = olvidar la elección: vuelve a quedar todo denegado y se pregunta de nuevo. */
export function revokeConsent(storage: StorageLike | null): void {
  try { storage?.removeItem(CONSENT_KEY); } catch { /* nada que hacer */ }
}

/** Avisa a Google del estado de consentimiento. */
export function applyConsent(gtag: GtagFn | undefined, choice: ConsentChoice): void {
  if (!gtag) return;
  gtag("consent", "update", consentSignals(choice));
}

/** Nombres de las cookies propias de GA4 (`_ga`, `_ga_XXXX`, `_gid`, `_gat*`) presentes. */
export function gaCookieNames(cookieString: string): string[] {
  return cookieString.split(";").map((c) => c.split("=")[0].trim()).filter((n) => n === "_ga" || n === "_gid" || n.startsWith("_ga_") || n.startsWith("_gat"));
}

/** Al rechazar o revocar se borran las cookies de GA4 que ya hubiera (en el dominio y en el dominio raíz). */
export function clearGaCookies(): void {
  if (typeof document === "undefined") return;
  const host = window.location.hostname;
  const parts = host.split(".");
  const domains = [host, parts.length > 2 ? `.${parts.slice(-2).join(".")}` : `.${host}`];
  for (const name of gaCookieNames(document.cookie)) {
    for (const d of domains) document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${d}`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  }
}

export function getBrowserStorage(): StorageLike | null {
  try { return typeof window !== "undefined" ? window.localStorage : null; } catch { return null; }
}

export function openCookiePreferences(): void {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(CONSENT_OPEN_EVENT));
}
